import { MediaImage } from "@/components/ui/MediaImage";
import type { MediaItem } from "@/data/media";
import type { Pillar } from "@/data/programs";
import type { Project } from "@/data/projects";
import type { Story } from "@/data/stories";
import { useMedia } from "@/content-context";
import { Link } from "react-router";

import { TextLink } from "./Button";

/** Arrow that swings to point out of the card on hover. */
function CornerArrow({ tone = "light" }: { tone?: "light" | "dark" }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-[background-color,border-color,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-rotate-45 ${
        tone === "light"
          ? "border-white/35 text-white group-hover:border-white group-hover:bg-white/12"
          : "border-line-strong text-navy group-hover:border-navy group-hover:bg-mist"
      }`}
    >
      <svg viewBox="0 0 16 16" className="h-4 w-4">
        <path
          d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/**
 * Pillar card.
 *
 * Full-bleed photograph with the type set on it, rather than a white panel on a
 * white page. A white card on a white section reads as nothing at all, which was
 * the flattest thing on the old homepage. Here the photograph is the card.
 *
 * The images are documentary, so they are never desaturated or filtered. The
 * reference card this borrows from greyscales at rest and saturates on hover;
 * that is a filter applied to photographs of real people, which the brief rules
 * out. The only hover moves here are a slow scale and the arrow swinging out.
 *
 * The scrim is computed, not eyeballed: measured against worst-case white pixels
 * underneath, the title reads at 8.6:1, the outcome at 7.1:1, the example line
 * at 6.5:1 and the link at 12.4:1, while 95% of the photograph still reads
 * across the top of the card.
 */
export function PillarCard({ pillar }: { pillar: Pillar }) {
  const image = pillar.cardImage ?? pillar.image;

  return (
    <article className="group relative isolate h-full overflow-hidden rounded-[24px] bg-midnight">
      <div className="relative aspect-[4/3] w-full sm:aspect-[16/11] lg:aspect-[4/3]">
        <MediaImage
          item={image}
          fill
          sizes="(min-width: 1024px) 46vw, 100vw"
          className="object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
          objectPosition={image.objectPosition ?? "50% 50%"}
        />

        <div
          aria-hidden="true"
          className="absolute inset-0 transition-opacity duration-700"
          style={{
            background:
              "linear-gradient(180deg, rgba(12,29,51,0.16) 0%, rgba(12,29,51,0.05) 20%, rgba(12,29,51,0.38) 40%, rgba(12,29,51,0.78) 53%, rgba(12,29,51,0.86) 74%, rgba(12,29,51,0.92) 100%)",
          }}
        />
      </div>

      <div className="absolute inset-0 flex flex-col justify-between p-7 md:p-9">
        <div className="flex justify-end">
          <CornerArrow />
        </div>

        <div>
          <h3 className="text-white" style={{ fontSize: "var(--step-h3)" }}>
            {pillar.title}
          </h3>
          <p className="mt-3.5 max-w-md text-[0.9375rem] leading-relaxed text-white/80">
            {pillar.outcome}
          </p>
          {pillar.examples.filter(Boolean).length ? (
            <p className="mt-5 text-[0.8125rem] text-white/70">{pillar.examples.filter(Boolean).join("  ·  ")}</p>
          ) : null}

          <span className="mt-6 inline-flex items-center gap-2 text-[0.875rem] font-semibold text-white">
            <Link to={`/our-work/${pillar.slug}`} className="after:absolute after:inset-0 after:content-['']">
              {pillar.cta}
            </Link>
          </span>
        </div>
      </div>
    </article>
  );
}

/**
 * Smaller cards used in three-column rows.
 *
 * These keep a light panel, because at this size type over a photograph stops
 * being readable. To stop them disappearing on white sections they now carry a
 * real resting shadow and a hairline border, rather than relying on hover alone.
 */
const panel =
  "group relative flex h-full flex-col overflow-hidden rounded-[20px] border border-line bg-white shadow-[0_1px_2px_rgba(12,29,51,0.04),0_12px_28px_-22px_rgba(12,29,51,0.22)] transition-[transform,box-shadow,border-color] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:border-line-strong hover:shadow-[0_1px_2px_rgba(12,29,51,0.05),0_28px_50px_-28px_rgba(12,29,51,0.34)]";

function PanelImage({ item, sizes }: { item: MediaItem; sizes: string }) {
  const image = useMedia()(item);
  return (
    <div className="relative aspect-[16/11] w-full overflow-hidden bg-mist">
      <MediaImage
        item={image}
        fill
        sizes={sizes}
        className="object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
        objectPosition={image.objectPosition ?? "50% 50%"}
      />
    </div>
  );
}

export function ProjectCard({ project }: { project: Project }) {
  return (
    <article className={panel}>
      <PanelImage item={project.image} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" />
      <div className="flex flex-1 flex-col p-6 md:p-7">
        <p className="text-[0.75rem] font-semibold uppercase tracking-[0.14em] text-blue">
          {project.status} · {project.location}
        </p>
        <h3 className="mt-3.5" style={{ fontSize: "var(--step-h4)" }}>
          {project.title}
        </h3>
        <p className="mt-2.5 text-[0.9375rem] text-muted">{project.summary}</p>
        <div className="mt-auto pt-6">
          <TextLink href={`/projects/${project.slug}`} stretch>
            Read the project
          </TextLink>
        </div>
      </div>
    </article>
  );
}

export function StoryCard({ story }: { story: Story }) {
  return (
    <article className={panel}>
      <PanelImage item={story.image} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" />
      <div className="flex flex-1 flex-col p-6 md:p-7">
        <p className="text-[0.75rem] font-semibold uppercase tracking-[0.14em] text-blue">
          {story.pillar}
        </p>
        <h3 className="mt-3.5" style={{ fontSize: "var(--step-h4)" }}>
          {story.title}
        </h3>
        <p className="mt-2.5 text-[0.9375rem] text-muted">{story.excerpt}</p>
        <p className="mt-5 text-[0.8125rem] text-muted/80">
          <time dateTime={story.isoDate}>{story.date}</time> · {story.location}
        </p>
        <div className="mt-auto pt-5">
          <TextLink href={`/stories/${story.slug}`} stretch>
            Read the story
          </TextLink>
        </div>
      </div>
    </article>
  );
}
